// src/UserContext.js
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { clearAuthToken } from "./api/api";
import { isNationwideRole, registerUserRoles } from "./utils/roleManagement";
import { clearActingCity, readActingCity, withActingCity } from "./utils/actingCity";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUserState] = useState(null);

  // Rol kataloğu (panelden eklenen roller, kapsamlar, giriş sayfaları) kullanıcı
  // state'e girmeden önce kaydedilir: ilk render'daki rota korumaları ve rol
  // adları güncel listeyi görsün.
  const setUser = useCallback((next) => {
    if (typeof next === "function") {
      setUserState((prev) => {
        const value = next(prev);
        registerUserRoles(value);
        return value;
      });
      return;
    }
    registerUserRoles(next);
    setUserState(next);
  }, []);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    const boot = async () => {
      try {
        // 1) Load cached user if exists (fast UI)
        const cached = localStorage.getItem("user");
        if (cached && alive) {
          setUser(JSON.parse(cached));
        }

        // 2) Verify cookie session (source of truth)
        const res = await api.get("/api/users/verify");
        if (!alive) return;

        setUser(res.data.user);
        localStorage.setItem("user", JSON.stringify(res.data.user));
      } catch (err) {
        if (!alive) return;
        // Ağ/sunucu hatasında oturumu düşürme; yalnızca kimlik gerçekten
        // reddedildiğinde (401/403) önbelleği temizle. Aksi halde geçici bir
        // API arızası kullanıcıyı panelden atıyor.
        if (![401, 403].includes(err?.response?.status)) return;
        setUser(null);
        localStorage.removeItem("user");
        clearAuthToken();
      } finally {
        if (!alive) return;
        setIsLoading(false);
      }
    };

    boot();
    return () => {
      alive = false;
    };
  }, [setUser]);

  // Türkiye geneli kullanıcı üst çubuktan il seçtiyse ekranlar kullanıcıyı o
  // ilin yetkilisi gibi görür (utils/actingCity.js). Seçim kalıcı state'te
  // değil tarayıcıda durur; değişince sayfa yeniden yüklenir.
  const [actingCity, setActingCity] = useState(readActingCity);
  const viewUser = useMemo(() => withActingCity(user, actingCity), [user, actingCity]);

  // Oturum kapandıysa ya da hesap Türkiye geneli değilse eski seçim düşer.
  useEffect(() => {
    if (isLoading || !actingCity) return;
    if (user && isNationwideRole(user.role)) return;
    clearActingCity();
    setActingCity(null);
  }, [user, isLoading, actingCity]);

  return <UserContext.Provider value={{ user: viewUser, setUser, isLoading, actingCity: viewUser?.actingCity || null }}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
export default UserContext;
